/**
 * Chat history, persisted in the browser.
 *
 * Transcripts are kept in localStorage rather than on the server. The agent's
 * durable *memory* (facts, preferences, per-session summaries) already lives in
 * AgentCore Memory, but there is no API to enumerate a user's past sessions or
 * replay their full transcripts — the runtime only exposes `/invocations`, and the
 * browser calls it directly with no proxy to add a history endpoint. Local storage
 * gives a usable "recent chats" panel without new infrastructure. The trade-off:
 * history is per-browser, not synced across devices, and clearing site data loses
 * it. Reopening a conversation restores its `sessionId`, so continuing it resumes
 * the same server-side memory thread.
 *
 * Scoped per user (by email) and filtered per agent by the caller, so switching
 * accounts or agents never surfaces another context's conversations.
 */

import type { ChatMessage } from "../components/Message";
import type { AgentName } from "./agui";

export interface Conversation {
  id: string;
  agent: AgentName;
  /** Derived from the first user message; shown in the side panel. */
  title: string;
  /** The runtime session id, so reopening resumes the same memory thread. */
  sessionId: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

const PREFIX = "edu-history:";

function storageKey(userKey: string): string {
  return `${PREFIX}${userKey}`;
}

export function loadConversations(userKey: string): Conversation[] {
  try {
    const raw = localStorage.getItem(storageKey(userKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as Conversation[])
      .filter((c) => c && typeof c.id === "string" && Array.isArray(c.messages))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    // Corrupt or unavailable storage (e.g. private mode): start empty.
    return [];
  }
}

function writeAll(userKey: string, conversations: Conversation[]): void {
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(conversations));
  } catch {
    // Quota exceeded or storage disabled; history is best-effort, never fatal.
  }
}

/** Insert or update a conversation, preserving its original createdAt. */
export function upsertConversation(
  userKey: string,
  convo: Conversation,
): Conversation[] {
  const all = loadConversations(userKey);
  const idx = all.findIndex((c) => c.id === convo.id);
  if (idx >= 0) {
    all[idx] = { ...convo, createdAt: all[idx].createdAt };
  } else {
    all.push(convo);
  }
  all.sort((a, b) => b.updatedAt - a.updatedAt);
  writeAll(userKey, all);
  return all;
}

export function deleteConversation(userKey: string, id: string): Conversation[] {
  const all = loadConversations(userKey).filter((c) => c.id !== id);
  writeAll(userKey, all);
  return all;
}

/** A short title from the first thing the user asked. */
export function titleFrom(messages: ChatMessage[]): string {
  const firstUser = messages.find((m) => m.role === "user");
  const text = firstUser?.text.trim() || "New conversation";
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}
